// Import the generated route tree
import { AuthProvider, useAuth } from 'react-oidc-context'

import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider, createRouter } from '@tanstack/react-router'

import { ErrorDisplay } from './components/ErrorDisplay'
import { LoadingSpinner } from './components/LoadingSpinner'
import { stringifySearch } from './lib/searchParams'
import type { PinStatus } from './modules/graphql/codegen/graphql'
import { useGraphqlAuth } from './modules/graphql/hooks/useGraphqlAuth'
import { logger } from './modules/utils'
import {
  attemptStaleStateRecovery,
  isStaleOidcStateError,
} from './pages/auth/oidcRecovery'
import { useAuthProps } from './pages/auth/useAuthProps'
import { routeTree } from './routeTree.gen'

const queryClient = new QueryClient()

export interface RouterContext {
  queryClient: QueryClient
  auth: ReturnType<typeof useAuth> | null
  permissions: string[]
  pinStatus: PinStatus | null
}

// Create a new router instance
export const router = createRouter({
  routeTree,
  context: {
    queryClient,
    auth: null,
    permissions: [],
    pinStatus: null,
  },
  defaultPreload: 'intent',
  defaultPendingComponent: LoadingSpinner,
  scrollRestoration: true,
  defaultStructuralSharing: true,
  defaultPreloadStaleTime: 0,
  stringifySearch,
})

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

function InnerApp() {
  const auth = useAuth()
  useGraphqlAuth()

  if (auth.isLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <LoadingSpinner />
      </div>
    )
  }

  if (auth.error) {
    if (isStaleOidcStateError(auth.error) && attemptStaleStateRecovery()) {
      return null
    }

    logger.error('Authentication error: %o', auth.error)
    return <ErrorDisplay error={auth.error.message} />
  }

  return (
    <RouterProvider
      router={router}
      context={{ auth, queryClient, permissions: [], pinStatus: null }}
    />
  )
}

export function App() {
  const authProps = useAuthProps()

  return (
    <AuthProvider {...authProps}>
      <QueryClientProvider client={queryClient}>
        <InnerApp />
      </QueryClientProvider>
    </AuthProvider>
  )
}
